import React, { useCallback, useMemo, useState } from "react";
import { shuffle, pick, precisionOf, starsOf, useCountdown, TimerBar, Hud } from "./nqShared";

const ITEMS = ["📘", "✏️", "🥤", "🍎", "📏", "🖍️", "📐", "🎒", "📗", "🧢", "🔦", "🗺️"];
const SIMILAR = { "📘": "📗", "📗": "📘", "✏️": "🖍️", "🖍️": "✏️", "📏": "📐", "📐": "📏", "🥤": "🧃", "🍎": "🍏", "🎒": "👜", "🧢": "🎩", "🔦": "🕯️", "🗺️": "📜" };
const CFG = { 1: { rows: 6, errors: 2, subtle: false, seconds: 0 }, 2: { rows: 9, errors: 3, subtle: true, seconds: 0 }, 3: { rows: 12, errors: 3, subtle: true, seconds: 60 } };

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Vérifie avant de dire « terminé »",
    goal: `trouver les ${c.errors} erreurs dans le sac.`,
    regarde: "À gauche : la liste modèle. À droite : ce qui a vraiment été mis dans le sac.",
    fais: "Compare chaque ligne. Touche celles où c'est différent (🚩), puis appuie sur ✅ Terminer.",
    attention: `STOP → SCAN → CORRIGE → TERMINE : ne dis pas « terminé » trop vite !${c.subtle ? " Les erreurs ressemblent beaucoup au modèle." : ""}${c.seconds ? ` Tu as ${c.seconds} secondes.` : ""}`,
    preview: { ok: ["📘 → 📗 = 🚩"], ko: ["📘 → 📘 = ⬜"], okLabel: "Une erreur à signaler", koLabel: "Une ligne correcte (ne rien faire)" },
  };
};

const build = (cfg) => {
  const models = shuffle(ITEMS).slice(0, cfg.rows);
  const errIdx = new Set(shuffle(models.map((_, i) => i)).slice(0, cfg.errors));
  return models.map((m, i) => {
    let copy = m;
    if (errIdx.has(i)) copy = cfg.subtle ? SIMILAR[m] || pick(ITEMS.filter((x) => x !== m)) : pick(ITEMS.filter((x) => x !== m && x !== SIMILAR[m]));
    return { id: i, model: m, copy, error: copy !== m, flagged: false };
  });
};

export default function GameScanner({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { rows: 4, errors: 1, subtle: false, seconds: 0 } : CFG[level.n]), [level, practice]);
  const [rows, setRows] = useState(() => build(cfg));
  const errors = useMemo(() => rows.filter((r) => r.error).length, [rows]);
  const flagged = rows.filter((r) => r.flagged).length;

  const finish = useCallback(() => {
    if (practice) return onPracticeEnd();
    const found = rows.filter((r) => r.error && r.flagged).length;
    const falseFlags = rows.filter((r) => !r.error && r.flagged).length;
    const precision = precisionOf(found, errors, falseFlags);
    return onFinish({ hits: found, total: errors, errors: falseFlags, precision, stars: starsOf(precision), detail: `${found}/${errors} erreur(s) repérée(s) · ${falseFlags} fausse(s) alerte(s)${found < errors ? " · certaines erreurs sont passées au scanner !" : ""}` });
  }, [rows, errors, onFinish, practice, onPracticeEnd]);
  const left = useCountdown(cfg.seconds || 9999, cfg.seconds > 0, finish);

  const flag = (id) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, flagged: !r.flagged } : r)));

  return (
    <div className="space-y-4" data-testid="nq-game-scanner">
      {cfg.seconds > 0 && <TimerBar left={left} total={cfg.seconds} color={power.color} />}
      <Hud items={[["Erreurs à trouver", errors, power.color], ["Lignes signalées 🚩", flagged, "#F2B84B"]]} />
      <div className="rounded-3xl bg-[#ECE6F7] p-4 space-y-2">
        <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 px-3 text-[11px] uppercase tracking-widest font-bold text-[var(--ink-soft)]"><span>📋 Liste modèle</span><span /><span>🎒 Dans le sac</span><span /></div>
        {rows.map((r) => (
          <button key={r.id} onClick={() => flag(r.id)} data-testid={`nq-scan-row-${r.id}`} data-error={r.error ? "1" : "0"}
            className={`w-full grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 rounded-2xl px-3 py-2 border-2 text-3xl transition-all active:scale-[0.98] ${r.flagged ? "bg-[#FDE4DF] border-[#E0705C]" : "bg-white border-transparent hover:border-[#8B6FC0]"}`}>
            <span>{r.model}</span><span className="text-sm text-[var(--ink-soft)]">→</span><span>{r.copy}</span><span className="text-base">{r.flagged ? "🚩" : "⬜"}</span>
          </button>
        ))}
        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="text-xs text-[var(--ink-soft)]">{flagged === 0 ? "Touche une ligne qui te semble fausse." : flagged < errors ? `Encore ${errors - flagged} à trouver ?` : "As-tu bien tout vérifié ?"}</span>
          <button onClick={finish} data-testid="nq-scan-finish" className="px-7 py-3 rounded-full text-white font-bold shadow-md active:scale-95 hover:brightness-105 transition-all" style={{ background: power.color }}>✅ Terminer la vérification</button>
        </div>
      </div>
    </div>
  );
}
